import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
//UI
import { Button, Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/react'
import { AiFillCloseSquare, AiOutlineMail, AiOutlineMenu } from 'react-icons/ai'
import { CgDetailsMore } from 'react-icons/cg'
import { FaClone } from 'react-icons/fa'
import { GrPowerCycle } from 'react-icons/gr'

//Constant
import { showAdminEditOfferModal, showCloneOfferModal, showReviseOfferModal } from '@/stores/offers/offersSlice'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { IoMdCreate } from 'react-icons/io'
import { MdOutlineEdit, MdPersonAddAlt1 } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { FiTrendingDown } from 'react-icons/fi'

import useOffer from '../../hooks/useOffer'
import useOfferAction from '../../hooks/useOfferAction'
import { showCloseOfferModal } from '../../slices/offerModal.slice'

const OfferListStatusActionMenu = ({
  offerStatus,
  offerType = 'offer',
  offerId,
  subSystemIndex = 0,
  salesId,
  projectName,
  //Control Flag
  showCloneButton = false,
  showAdminButtons = false,

  //Maintenance Summary Use
  showDetailButton = false,
  showRenewButton = false,
  showRenewalLostButton = false,
  showUndoRenewalLostButton = false,

  //Offer List used
  showActivateButton = false,
  showNoticeApproverButton = false,
  showReviseButton = false,
  showCreateBookingButton = false,
  showAddCustomerButton = false,
  showClosedButton = false,
  showUnclosedButton = false,
  onRemoveOffer,
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { useRemoveOffer } = useOffer()
  const {
    useCreateOfferBooking,
    useNoticeCurrentApprover,
    useActivateOffer,
    useAddCustomerToOffer,
    useToggleOfferRenewalLost,
    useUncloseOffer,
  } = useOfferAction()

  const removeOffer = useRemoveOffer()
  const createOfferBooking = useCreateOfferBooking()
  const noticeCurrentApprover = useNoticeCurrentApprover()
  const activateOffer = useActivateOffer()
  const addCustomerToOffer = useAddCustomerToOffer()
  const toggleOfferRenewalLost = useToggleOfferRenewalLost()
  const uncloseOffer = useUncloseOffer()

  const handleGotoOffer = useCallback(
    (e) => {
      e.stopPropagation()
      navigate(`/offers/${offerId}`)
    },
    [navigate, offerId]
  )

  const handleCloneOffer = useCallback(
    (e) => {
      e.stopPropagation()
      if (!offerId || offerId === '') {
        return
      }
      dispatch(
        showCloneOfferModal({
          offerId: offerId,
          salesId: salesId,
          isRenew: false,
        })
      )
    },
    [dispatch]
  )

  const handleRenewOffer = (e) => {
    e.stopPropagation()

    if (!offerId || offerId === '') {
      return
    }
    dispatch(
      showCloneOfferModal({
        offerId: offerId,
        salesId: salesId,
        subSystemIndex: subSystemIndex,
        isRenew: true,
      })
    )
  }

  const handleAddCustomerToOffer = useCallback(
    async (e) => {
      e.stopPropagation()

      try {
        dispatch(showLoadingOverlay())
        const res = await addCustomerToOffer.mutateAsync({ offerId })
      } catch (err) {
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [addCustomerToOffer, offerId]
  )

  const handleReviseOffer = useCallback(
    (e) => {
      e.stopPropagation()

      if (offerId && offerId !== '') {
        dispatch(
          showReviseOfferModal({
            offerId: offerId,
            projectName: projectName,
          })
        )
      }
    },
    [dispatch, offerId, projectName]
  )

  const handleDeleteOffer = useCallback(
    (e) => {
      e.stopPropagation()

      if (offerId && offerId !== '') {
        removeOffer.mutate(offerId)
      }
    },
    [removeOffer, offerId]
  )

  const handleCreateBooking = useCallback(
    async (e) => {
      e.stopPropagation()

      try {
        dispatch(showLoadingOverlay())
        const res = await createOfferBooking.mutateAsync({ offerId })

        navigate(`/offers/${offerId}`)
      } catch (err) {
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [createOfferBooking, navigate, offerId]
  )

  const handleActivateOffer = useCallback(
    async (e) => {
      e.stopPropagation()

      try {
        dispatch(showLoadingOverlay())
        const res = await activateOffer.mutateAsync({ offerId })

        const newOfferId = res?._id
        if (newOfferId && newOfferId !== '') {
          navigate(`/offers/${newOfferId}`)
        }
      } catch (err) {
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [activateOffer, offerId]
  )

  const handleNoticeCurrentApprover = useCallback(
    async (e) => {
      e.stopPropagation()

      try {
        dispatch(showLoadingOverlay())
        const res = await noticeCurrentApprover.mutateAsync({ offerId })
      } catch (err) {
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [noticeCurrentApprover, offerId]
  )

  const handleAdminEdit = useCallback(
    (e) => {
      e.stopPropagation()

      dispatch(
        showAdminEditOfferModal({
          offerId: offerId,
        })
      )
    },
    [dispatch, offerId]
  )

  const handleToggleRenewalLost = useCallback(
    (e) => {
      e.stopPropagation()
      dispatch(
        showCloseOfferModal({
          show: true,
          offerId,
          type: 'maintenance',
        })
      )
    },
    [dispatch, offerId]
  )

  const handleToggleUndoRenewalLost = useCallback(async () => {
    if (!offerId || offerId == '') {
      return
    }

    try {
      const res = await toggleOfferRenewalLost.mutateAsync({ offerId })
    } catch (err) {
      console.error(err)
    }
  }, [offerId])

  const handleCloseOffer = useCallback(
    (e) => {
      e.stopPropagation()

      dispatch(
        showCloseOfferModal({
          show: true,
          offerId,
          type: 'offer',
        })
      )
    },
    [dispatch, offerId]
  )
  const handleUncloseOffer = useCallback(
    async (e) => {
      e.stopPropagation()
      if (!offerId || offerId == '') {
        return
      }

      try {
        const res = await uncloseOffer.mutateAsync({ offerId })
      } catch (err) {
        console.error(err)
      }
    },
    [dispatch, offerId]
  )

  return (
    <Menu strategy="fixed" placement="right" matchWidth={true} offset={[0, 15]}>
      <MenuButton as={Button} colorScheme="blue" size="sm" onClick={(e) => e.stopPropagation()}>
        <AiOutlineMenu />
      </MenuButton>

      <MenuList zIndex={3}>
        {showDetailButton && (
          <MenuItem icon={<CgDetailsMore size={18} />} onClick={handleGotoOffer}>
            Detail
          </MenuItem>
        )}

        {showCloneButton && (
          <MenuItem icon={<FaClone size={18} />} onClick={handleCloneOffer}>
            Clone
          </MenuItem>
        )}
        {showRenewButton && (
          <MenuItem icon={<FaClone size={18} />} onClick={handleRenewOffer}>
            Renew
          </MenuItem>
        )}

        {showAddCustomerButton && (
          <MenuItem icon={<MdPersonAddAlt1 size={18} />} onClick={handleAddCustomerToOffer}>
            Add Customer
          </MenuItem>
        )}

        {showClosedButton && (
          <MenuItem icon={<AiFillCloseSquare size={18} />} onClick={handleCloseOffer}>
            Close
          </MenuItem>
        )}
        {showUnclosedButton && (
          <MenuItem icon={<AiFillCloseSquare size={18} />} onClick={handleUncloseOffer}>
            Unclose
          </MenuItem>
        )}

        {showReviseButton && (
          <MenuItem icon={<GrPowerCycle size={18} />} onClick={handleReviseOffer}>
            Revise
          </MenuItem>
        )}
        {showCreateBookingButton && (
          <MenuItem icon={<IoMdCreate size={18} />} onClick={handleCreateBooking}>
            Create Booking
          </MenuItem>
        )}
        {showActivateButton && (
          <MenuItem icon={<GrPowerCycle size={18} />} onClick={handleActivateOffer}>
            Activate
          </MenuItem>
        )}
        {showNoticeApproverButton && (
          <MenuItem icon={<AiOutlineMail size={18} />} onClick={handleNoticeCurrentApprover}>
            Notice Current Approver
          </MenuItem>
        )}

        {showAdminButtons && (
          <>
            <MenuItem icon={<MdOutlineEdit size={18} />} onClick={handleAdminEdit}>
              Admin Edit
            </MenuItem>
            {/* <MenuItem icon={<AiFillDelete size={18} />} onClick={handleDeleteOffer}>
              Delete
            </MenuItem> */}
          </>
        )}
        {showRenewalLostButton && (
          <MenuItem icon={<MdOutlineEdit size={18} />} onClick={handleToggleRenewalLost}>
            Renewal Lost
          </MenuItem>
        )}
        {showUndoRenewalLostButton && (
          <MenuItem icon={<MdOutlineEdit size={18} />} onClick={handleToggleUndoRenewalLost}>
            Undo Renewal Lost
          </MenuItem>
        )}
      </MenuList>
    </Menu>
  )
}

export default OfferListStatusActionMenu
